import React, { useState, useRef, useEffect } from 'react';
import { Home, Building2, Heart, Users, ChevronLeft, ChevronRight } from 'lucide-react';
import type_A from '../../assets/floorplans/floorplan_a.webp';
import type_B from '../../assets/floorplans/floorplan_b.webp';
import type_C from '../../assets/floorplans/floorplan_c.webp';
import type_D from '../../assets/floorplans/floorplan_d.webp';
import type_E from '../../assets/floorplans/floorplan_e.webp';
import type_F from '../../assets/floorplans/floorplan_f.webp';
import type_G from '../../assets/floorplans/floorplan_g.webp';
import type_H from '../../assets/floorplans/floorplan_h.webp';
import type_A1 from '../../assets/floorplans/floorplan_a1.webp';
import type_B1 from '../../assets/floorplans/floorplan_b1.webp';
import wellness_GF from '../../assets/floorplans/wellness_centre_gf.jpg';
import wellness_FF from '../../assets/floorplans/wellness_centre_ff.jpg';
import clubhouse_GF from '../../assets/floorplans/clubhouse_gf.jpg';
import clubhouse_FF from '../../assets/floorplans/clubhouse_ff.jpg';
import master_plan from '../../assets/floorplans/masterplan_dignity.jpg';
import FloorPlansPopup from '../FloorPlansPopup';

const FloorPlans = ({ className = "" }) => {
  const [activeCategory, setActiveCategory] = useState('master');
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [currentSlide, setCurrentSlide] = useState(0);
  const scrollContainerRef = useRef(null);

  const categories = [
    { id: 'master', label: 'Master Plan', icon: Building2 },
    { id: '1bhk', label: 'Villa 1 BHK', icon: Home },
    { id: '2bhk', label: 'Villa 2 BHK', icon: Home },
    { id: 'wellness', label: 'Wellness Centre', icon: Heart },
    { id: 'clubhouse', label: 'Club House', icon: Users }
  ];

  const floorPlans = {
    master: [
      { name: 'Master Plan', image: master_plan }
    ],
    '1bhk': [
      { name: 'Type C', image: type_C },
      { name: 'Type D', image: type_D },
      { name: 'Type E', image: type_E },
      { name: 'Type H', image: type_H }
    ],
    '2bhk': [
      { name: 'Type A', image: type_A },
      { name: 'Type A1', image: type_A1 },
      { name: 'Type B', image: type_B },
      { name: 'Type B1', image: type_B1 },
      { name: 'Type F', image: type_F },
      { name: 'Type G', image: type_G }
    ],
    wellness: [
      { name: 'Ground Floor', image: wellness_GF },
      { name: 'First Floor', image: wellness_FF }
    ],
    clubhouse: [
      { name: 'Ground Floor', image: clubhouse_GF },
      { name: 'First Floor', image: clubhouse_FF }
    ]
  };

  const currentPlans = floorPlans[activeCategory] || [];

  const handleImageClick = (image, index) => {
    setSelectedImage(image);
    setCurrentImageIndex(index);
  };

  const handlePopupNavigate = (newIndex) => {
    setCurrentImageIndex(newIndex);
    setSelectedImage(currentPlans[newIndex]);
  };

  // Handle scroll progress for villa plans
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer || (activeCategory !== '1bhk' && activeCategory !== '2bhk')) return;

    const handleScroll = () => {
      const scrollWidth = scrollContainer.scrollWidth - scrollContainer.clientWidth;
      const scrollLeft = scrollContainer.scrollLeft;
      const progress = scrollWidth > 0 ? (scrollLeft / scrollWidth) * 100 : 0;
      setScrollProgress(progress);
      
      // Calculate current slide based on scroll position
      const cardWidth = 320; // w-80 = 320px
      const scrollPosition = scrollContainer.scrollLeft;
      const slide = Math.round(scrollPosition / cardWidth);
      setCurrentSlide(slide);
    };

    scrollContainer.addEventListener('scroll', handleScroll);
    return () => scrollContainer.removeEventListener('scroll', handleScroll);
  }, [activeCategory]);

  const scrollToSlide = (direction) => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const cardWidth = 320; // w-80 = 320px
    const gap = 24; // gap-6 = 24px
    const scrollAmount = (cardWidth + gap) * direction;

    scrollContainer.scrollBy({
      left: scrollAmount,
      behavior: 'smooth'
    });
  };

  return (
    <div className={`pt-16 sm:pt-28 ${className}`}>
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 sm:mb-16">
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-light text-gray-900 mb-6 tracking-tight leading-tight">
            Elegant Spaces Crafted for
            <span className="block text-[var(--primarycolor)] font-normal mt-2">Comfort and Dignity</span>
        </h2>
        
        <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Explore well-designed floor plans that balance style, functionality, and everyday ease at Dignity Living.
        </p>
        </div>

        {/* Category Tabs */}
        <div className="mb-12">
          <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
            {categories.map((category) => {
              const IconComponent = category.icon;
              const isActive = activeCategory === category.id;
              
              return (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  className={`
                    flex items-center gap-2 px-4 sm:px-6 py-3 sm:py-3.5 rounded-xl font-medium transition-all duration-300
                    ${isActive 
                      ? 'bg-[var(--primarycolor)] text-white scale-105' 
                      : 'bg-white text-gray-700 border border-gray-200 hover:border-[var(--primarycolor)]/30 hover:bg-red-50'
                    }
                  `}
                >
                  <IconComponent className={`w-4 h-4 sm:w-5 sm:h-5 ${isActive ? 'text-white' : 'text-[var(--primarycolor)]'}`} />
                  <span className="text-sm sm:text-base">{category.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Innovative Staggered Layout */}
        {activeCategory === 'master' ? (
          // Master Plan - Single Large Display with consistent height
          <div className="max-w-5xl mx-auto">
            <div 
              onClick={() => handleImageClick(currentPlans[0], 0)}
              className="relative group cursor-pointer"
            >
              <div className="rounded-3xl overflow-hidden border-2 border-gray-200 hover:border-[var(--primarycolor)] transition-all duration-500">
                <img
                  src={currentPlans[0].image}
                  alt={currentPlans[0].name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
              </div>
            </div>
          </div>
        ) : activeCategory === 'wellness' || activeCategory === 'clubhouse' ? (
          // Wellness & Clubhouse - Side by Side with consistent height
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {currentPlans.map((plan, index) => (
              <div
                key={index}
                onClick={() => handleImageClick(plan, index)}
                className="relative group cursor-pointer"
              >
                <div className="aspect-[4/3] rounded-2xl overflow-hidden border-2 border-gray-200 hover:border-[var(--primarycolor)] transition-all duration-500">
                  <img
                    src={plan.image}
                    alt={plan.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
              </div>
            ))}
          </div>
        ) : (
          // Villa Plans - Simple Horizontal Scroll with Cards
          <div className="relative">
            {/* Scroll Navigation Buttons */}
            {currentPlans.length > 1 && (
              <>
                <button
                  onClick={() => scrollToSlide(-1)}
                  disabled={currentSlide === 0}
                  className="absolute left-2 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white/90 hover:bg-white border border-gray-200 rounded-full flex items-center justify-center transition-all duration-300 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-5 h-5 text-gray-700" />
                </button>
                <button
                  onClick={() => scrollToSlide(1)}
                  disabled={currentSlide === currentPlans.length - 1}
                  className="absolute right-2 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-white/90 hover:bg-white border border-gray-200 rounded-full flex items-center justify-center transition-all duration-300 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-5 h-5 text-gray-700" />
                </button>
              </>
            )}

            <div 
              ref={scrollContainerRef}
              className="flex gap-6 overflow-x-auto pb-4 scrollbar-hide snap-x snap-mandatory px-12"
            >
              {currentPlans.map((plan, index) => (
                <div
                  key={index}
                  onClick={() => handleImageClick(plan, index)}
                  className="flex-shrink-0 w-80 snap-center group cursor-pointer"
                >
                  <div className="relative">
                    {/* Main Card */}
                    <div className="bg-white rounded-2xl overflow-hidden border-2 border-gray-200 hover:border-[var(--primarycolor)] transition-all duration-500">
                      {/* Image */}
                      <div className="bg-gray-100 overflow-hidden relative">
                        <img
                          src={plan.image}
                          alt={plan.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      
                      {/* Type Label */}
                      <div className="pb-2 text-center">
                        <h3 className="text-lg font-semibold text-gray-900 group-hover:text-[var(--primarycolor)] transition-colors duration-300">
                          {plan.name}
                        </h3>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Modern Stunning Scroll Indicator */}
            {currentPlans.length > 1 && (
              <div className="flex justify-center mt-8">
                <div className="relative w-80 max-w-md">
                  {/* Background track */}
                  <div className="h-0.5 bg-gradient-to-r from-transparent via-gray-300 to-transparent rounded-full overflow-hidden">
                    {/* Active progress bar */}
                    <div 
                      className="h-full bg-gradient-to-r from-[var(--primarycolor)] via-red-500 to-[var(--primarycolor)] rounded-full transition-all duration-300 ease-out relative"
                      style={{ 
                        width: `${Math.max(scrollProgress, 8)}%`,
                        boxShadow: '0 0 12px rgba(239, 68, 68, 0.4)'
                      }}
                    >
                      {/* Animated glow effect */}
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent animate-shimmer"></div>
                    </div>
                  </div>
                  
                  {/* Slide indicator dots */}
                  {/* <div className="flex justify-between absolute -top-2 left-0 right-0 px-1">
                    {currentPlans.map((_, index) => {
                      const position = (index / (currentPlans.length - 1)) * 100;
                      return (
                        <div
                          key={index}
                          className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
                            index === currentSlide 
                              ? 'bg-[var(--primarycolor)] scale-150' 
                              : 'bg-gray-300'
                          }`}
                          style={{ 
                            position: 'absolute',
                            left: `${position}%`,
                            transform: 'translateX(-50%)'
                          }}
                        />
                      );
                    })}
                  </div> */}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Image Popup */}
      <FloorPlansPopup 
        image={selectedImage} 
        onClose={() => setSelectedImage(null)}
        allImages={currentPlans}
        currentIndex={currentImageIndex}
        onNavigate={handlePopupNavigate}
      />

      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        
        @keyframes shimmer {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }
        
        .animate-shimmer {
          animation: shimmer 2s infinite;
        }
      `}</style>
    </div>
  );
};

export default FloorPlans